import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, ShieldAlert, ShieldCheck, RefreshCw, AlertTriangle } from 'lucide-react';
import api from '@/services/api';
import DeploymentHistoryPanel from './DeploymentHistoryPanel';
import RecentErrorsPanel from './RecentErrorsPanel';
import AlertingConfigPanel from './AlertingConfigPanel';

/**
 * Admin → System Health tab.
 *
 * Compares runtime environment values (what the FastAPI process actually
 * sees) against the ``/app/backend/.env`` file on disk. Highlights any
 * mismatch — the exact failure mode that caused the Google OAuth
 * ``redirect_uri_mismatch`` in iteration 108, where Kubernetes was
 * injecting a stale ``APP_URL`` that overrode ``.env``.
 */
export const AdminSystemHealthTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/system-health');
      setData(r?.data || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load system health');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-system-health-tab">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <CardTitle className="text-slate-900 flex items-center gap-2">
            {data?.mismatch_count > 0 ? (
              <ShieldAlert className="w-5 h-5 text-red-600" />
            ) : (
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            )}
            System Health
          </CardTitle>
          <CardDescription className="text-slate-500 mt-1">
            Runtime environment values vs your <code className="text-xs">/app/backend/.env</code> file.
            Mismatches usually mean the hosting platform is injecting a stale value that overrides
            your .env — the cause of subtle OAuth &amp; database misconfig bugs.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="system-health-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Refresh</span>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800">{error}</div>
        ) : (
          <>
            {data?.mismatch_count > 0 && (
              <div className="p-3 rounded-lg border-2 border-red-300 bg-red-50" data-testid="system-health-mismatch-banner">
                <p className="text-sm font-semibold text-red-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {data.mismatch_count} configuration mismatch{data.mismatch_count > 1 ? 'es' : ''} detected
                </p>
                <p className="text-xs text-red-800 mt-1">
                  Your hosting platform is injecting values that override <code>.env</code>. For OAuth flows use
                  {' '}<code>GOOGLE_REDIRECT_BASE</code> which the platform doesn&apos;t override, or contact your
                  platform admin to update the environment variables.
                </p>
              </div>
            )}
            {data?.platform_override_count > 0 && data?.mismatch_count === 0 && (
              <div className="p-3 rounded-lg border border-blue-200 bg-blue-50" data-testid="system-health-platform-info">
                <p className="text-sm font-semibold text-blue-900">
                  {data.platform_override_count} value{data.platform_override_count > 1 ? 's are' : ' is'} being injected by your hosting platform — this is expected in production.
                </p>
                <p className="text-xs text-blue-800 mt-1">
                  Keys like <code>MONGO_URL</code> and <code>DB_NAME</code> intentionally use the platform-managed value; the <code>.env</code> row is only a local-dev fallback.
                </p>
              </div>
            )}
            {!data?.has_dotenv && (
              <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
                No <code>.env</code> file found on disk — comparison is not possible in this environment.
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 font-medium">Key</th>
                    <th className="py-2 font-medium">Runtime value</th>
                    <th className="py-2 font-medium">.env file value</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.rows || []).map((r) => (
                    <tr
                      key={r.key}
                      className="border-b border-slate-100 last:border-0 align-top"
                      data-testid={`system-health-row-${r.key}`}
                    >
                      <td className="py-2.5 pr-4 font-mono text-xs text-slate-800">
                        <div className="flex items-center gap-2">
                          {r.key}
                          {r.critical && <span className="text-red-500" title="Critical">*</span>}
                        </div>
                        {r.notes && <p className="text-[10px] text-slate-500 font-sans mt-0.5">{r.notes}</p>}
                      </td>
                      <td className="py-2.5 pr-4 font-mono text-xs text-slate-700 break-all">
                        {r.runtime}
                      </td>
                      <td className="py-2.5 pr-4 font-mono text-xs text-slate-500 break-all">
                        {r.env_file}
                      </td>
                      <td className="py-2.5 whitespace-nowrap">
                        {r.status === 'mismatch' ? (
                          <Badge className="bg-red-100 text-red-800 border-red-200" data-testid={`system-health-mismatch-${r.key}`}>
                            Mismatch
                          </Badge>
                        ) : r.status === 'platform_injected' ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200" data-testid={`system-health-platform-${r.key}`} title={r.override_hint || ''}>
                            Platform-injected
                          </Badge>
                        ) : r.status === 'missing' ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                            Not set
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            OK
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-500">
              * Critical keys — mismatches here typically block core flows (auth, OAuth, DB).
              Secret values are masked (first 4 + last 4 chars).
            </p>

            {/* DB-backed dynamic configs (Stripe / Paddle / SMTP) — these
                live in Mongo instead of .env, so a missing env var here is
                not a signal that the service is broken. Surfacing them
                here gives admins one screen for "what's configured?". */}
            {data?.dynamic_configs?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200" data-testid="dynamic-configs-panel">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Managed integrations</h4>
                <p className="text-xs text-slate-500 mb-3">
                  Configured via the DB — not <code>.env</code>. Toggle from the linked admin page.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {data.dynamic_configs.map((cfg) => (
                    <div
                      key={cfg.name}
                      className="p-3 rounded-lg border border-slate-200 bg-slate-50"
                      data-testid={`dynamic-config-${cfg.name.toLowerCase()}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-900">{cfg.name}</span>
                        {cfg.configured ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                            {cfg.mode ? cfg.mode.toUpperCase() : 'Active'}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                            Not configured
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">{cfg.location}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{cfg.notes}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
    <DeploymentHistoryPanel />
    <RecentErrorsPanel />
    <AlertingConfigPanel />
    </div>
  );
};

export default AdminSystemHealthTab;
